import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { DayPickerRangeController } from 'react-dates'
import styled from 'styled-components'
import { DatePickerTrigger } from './DatePickerTrigger'
import { MonthYearSelect } from './MonthYearSelect'
import { RenderPresets } from './RenderPresets'

const DatePickerContainer = styled.div`
  .DayPicker {
    box-shadow: none !important;
    min-width: initial !important;

    .CalendarMonth {
      background: transparent !important;
    }
    .CalendarMonthGrid {
      background: transparent !important;
    }

    .DayPicker_focusRegion {
      min-width: initial !important;
    }
  }
`

export const DateRangePicker = ({
  dates,
  onDateChange,
  formatDate,
  startRange,
  endRange,
}: {
  dates: { start: string; end: string }
  onDateChange: (start: string, end: string) => void
  formatDate?: (date?: Date | null) => string
  startRange?: Date
  endRange?: Date
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [startDate, setStartDate] = useState<moment.Moment | null>(null)
  const [endDate, setEndDate] = useState<moment.Moment | null>(null)
  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >('startDate')

  useEffect(() => {
    setStartDate(moment(dates.start))
    setEndDate(moment(dates.end))
  }, [dates.end, dates.start])

  const handleDateChange = useCallback(
    (startDate: moment.Moment, endDate: moment.Moment) => {
      const start = startDate.format('YYYY-MM-DD')
      const end = endDate.format('YYYY-MM-DD')
      onDateChange(start, end)
      onClose()
    },
    [onDateChange, onClose]
  )

  const applyDateChange = useCallback(() => {
    if (startDate && endDate) {
      handleDateChange(startDate, endDate)
    }
  }, [endDate, handleDateChange, startDate])

  const handlePresetClick = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: moment.Moment
      endDate: moment.Moment
    }) => {
      handleDateChange(startDate, endDate)
    },
    [handleDateChange]
  )

  const handleDatesChange = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: moment.Moment | null
      endDate: moment.Moment | null
    }) => {
      setStartDate(startDate)
      setEndDate(endDate)
    },
    []
  )

  const handleFocusChange = useCallback(
    (focusedInput: 'startDate' | 'endDate' | null) => {
      setFocusedInput(focusedInput || 'startDate')
    },
    []
  )

  return (
    <>
      <Box onClick={onOpen} cursor='pointer' px={2}>
        <DatePickerTrigger
          startDate={new Date(dates.start)}
          endDate={new Date(dates.end)}
          formatDate={formatDate}
        />
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width='initial' maxW='none'>
          <ModalBody pb={6} p={0}>
            <DatePickerContainer>
              <DayPickerRangeController
                startDate={startDate || moment(dates.start)}
                endDate={endDate || moment(dates.end)}
                onDatesChange={handleDatesChange}
                focusedInput={focusedInput}
                onFocusChange={handleFocusChange}
                renderMonthElement={(props) => (
                  <MonthYearSelect
                    fromYear={startRange?.getFullYear()}
                    {...props}
                  />
                )}
                isOutsideRange={(day) => day.isSameOrBefore(moment(startRange))}
                numberOfMonths={2}
                hideKeyboardShortcutsPanel={true}
                initialVisibleMonth={() => startDate || moment(dates.start)}
              />
            </DatePickerContainer>

            <Box
              m={4}
              mt={0}
              display='flex'
              justifyContent='space-between'
              alignItems='flex-end'
            >
              <RenderPresets
                onPresetClick={handlePresetClick}
                maxDataRange={[moment(startRange), moment(endRange)]}
              />
              <Button
                colorScheme='blue'
                onClick={applyDateChange}
                size='xs'
                isDisabled={!startDate || !endDate}
              >
                Apply
              </Button>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
